/**
 * Utility functions for extracting and transforming global elements data
 * from the Optimizely CMS 'grg-web-uk-global' page
 */
import {generateStyles} from '../components/optimizely/utils/utils'

/**
 * Extract header data from the global CMS content
 * @param {Object} globalContent - The global content from Optimizely CMS
 * @returns {Object} Extracted header data
 */
export const extractGlobalHeaderData = (globalContent) => {
    if (!globalContent?.items?.length) {
        return null
    }

    const globalItem = globalContent.items[0]
    const headerRef = globalItem?.HeaderReference?.Expanded

    if (!headerRef) {
        return null
    }

    // Extract logo data
    const logo = headerRef.LogoMedia?.Expanded?.Url ? {
        url: headerRef.LogoMedia.Expanded.Url,
        altText: headerRef.LogoMediaAltText || 'Logo'
    } : null

    // Extract responsive logo if available
    const responsiveLogo = headerRef.ResponsiveLogoMedia?.Expanded?.Url ? {
        url: headerRef.ResponsiveLogoMedia.Expanded.Url,
        altText: headerRef.LogoMediaAltText || 'Logo'
    } : null

    // Extract search placeholder
    const searchPlaceholder = headerRef.SearchPlaceholderText || 'Search'

    // Extract icon navigation
    const iconNavigation = headerRef.IconNavList?.map(iconItem => {
        const expanded = iconItem.Expanded
        if (!expanded) return null

        return {
            id: iconItem.Id,
            icon: expanded.Icon?.Expanded?.Url ? {
                url: expanded.Icon.Expanded.Url,
                altText: expanded.IconMediaAltText || ''
            } : null,
            text: expanded.CtaLinkText || '',
            title: expanded.Title || '',
            description: expanded.Description || '',
            link: expanded.Link?.Expanded ? {
                screenId: expanded.Link.Expanded.ScreenId,
                text: expanded.Link.Expanded.Text || '',
                url: expanded.Link.Expanded.Url || null
            } : null,
            styling: {
                textColor: expanded.TextColor || 'black',
                alignment: expanded.Alignment || '',
                underline: expanded.Underline || false,
                backgroundColor: expanded.CTABackgroundColor || ''
            }
        }
    }).filter(Boolean) || []

    // Extract banner data
    const banner = {
        message: headerRef.HeaderBannerMessage || '',
        messageStyle: headerRef.HeaderBannerMessageStyle || {},
        cta: headerRef.HeaderBannerCta?.Expanded ? {
            text: headerRef.HeaderBannerCta.Expanded.CtaLinkText || '',
            link: headerRef.HeaderBannerCta.Expanded.Link?.Expanded || null,
            styling: {
                textColor: headerRef.HeaderBannerCta.Expanded.TextColor || 'black',
                underline: headerRef.HeaderBannerCta.Expanded.Underline || false
            }
        } : null
    }

    // Extract styling
    const styling = {
        backgroundColor: headerRef.HeaderBackgroundStyle || 'white',
        defaultMenuTextStyle: generateStyles(headerRef.DefaultMenuTextStyle || {})
    }

    return {
        logo,
        responsiveLogo,
        search: {
            placeholder: searchPlaceholder
        },
        iconNavigation,
        banner,
        styling,
        raw: headerRef
    }
}

/**
 * Extract search data from the global CMS content
 * @param {Object} globalContent - The global content from Optimizely CMS
 * @returns {Object} Extracted search data
 */
export const extractGlobalSearchData = (globalContent) => {
    if (!globalContent?.items?.length) {
        return null
    }

    const globalItem = globalContent.items[0]
    
    // Extract search empty message
    const searchEmptyMessage = globalItem.SearchEmptyMessageReference?.Expanded
    
    // Extract search content area (search recommendations, etc.)
    const searchContentArea = globalItem.SearchContentArea?.map(item => ({
        id: item.ContentLink?.Id,
        type: item.ContentLink?.Expanded?.__typename,
        displayOption: item.DisplayOption,
        tag: item.Tag,
        expanded: item.ContentLink?.Expanded
    })).filter(Boolean) || []

    return {
        emptyMessage: searchEmptyMessage ? {
            searchBarMessage: searchEmptyMessage.SearchBarEmptyMessage || '',
            searchBarMessageStyle: searchEmptyMessage.SearchBarEmptyMessageStyle || {},
            searchResultMessage: searchEmptyMessage.SearchResultEmptyMessage || '',
            searchResultMessageStyle: searchEmptyMessage.SearchResultEmptyMessageStyle || {}
        } : null,
        contentArea: searchContentArea,
        raw: {
            searchEmptyMessage,
            searchContentArea: globalItem.SearchContentArea
        }
    }
}

/**
 * Extract footer data from the global CMS content
 * @param {Object} globalContent - The global content from Optimizely CMS
 * @returns {Object} Extracted footer data
 */
export const extractGlobalFooterData = (globalContent) => {
    if (!globalContent?.items?.length) {
        return null
    }

    const globalItem = globalContent.items[0]
    const footerRef = globalItem?.FooterReference?.Expanded

    if (!footerRef) {
        return null
    }

    // TODO: Add footer data extraction logic when FooterReference structure is available
    return {
        raw: footerRef
    }
}

/**
 * Extract product badges data from the global CMS content
 * @param {Object} globalContent - The global content from Optimizely CMS
 * @returns {Array} Array of badge configurations
 */
export const extractProductBadgesData = (globalContent) => {
    if (!globalContent?.items?.length) {
        return []
    }

    const globalItem = globalContent.items[0]
    const badgeArea = globalItem.OnImageBadgeContentArea

    if (!badgeArea?.length) {
        return []
    }

    return badgeArea.map(badgeItem => {
        const expanded = badgeItem.ContentLink?.Expanded
        if (!expanded) return null

        return {
            id: expanded.BadgeId || '',
            text: expanded.BadgeText || '',
            textStyle: generateStyles(expanded.BadgeTextStyle || {}),
            backgroundColor: expanded.BackgroundColorPicker || expanded.BackgroundColor || '',
            opacity: expanded.Opacity || 100,
            padding: expanded.PaddingPerDevice || {},
            width: expanded.OnImageBadgeBlockWidth || 100,
            height: expanded.Height || 25,
            borderRadius: expanded.BorderRadius || 0,
            displayOption: badgeItem.DisplayOption || '',
            tag: badgeItem.Tag || null,
            HideOnDevices: expanded.HideOnDevices,
            raw: expanded
        }
    }).filter(Boolean)
}


export const extractNavigationData = (globalContent) => {
    if (!globalContent?.items?.length) {
        return null
    }

    const globalItem = globalContent.items[0]
    const headerRef = globalItem?.HeaderReference?.Expanded

    if (!headerRef) {
        return null
    }

    return {
        CategoryReplacement: headerRef?.CategoryReplacement || null,
        ShopMenuContent: headerRef?.ShopMenuContent || null,
    }
}

/**
 * Extract all global elements data from the CMS content
 * @param {Object} globalContent - The global content from Optimizely CMS
 * @returns {Object} All extracted global elements
 */
export const extractAllGlobalElements = (globalContent) => {
    return {
        header: extractGlobalHeaderData(globalContent),
        search: extractGlobalSearchData(globalContent),
        footer: extractGlobalFooterData(globalContent),
        badges: extractProductBadgesData(globalContent),
        navigation: extractNavigationData(globalContent),
    }
}